import { useImperativeHandle, useState, forwardRef } from "react";
import InputBottomBorder from "components/common/Input/InputBottomBorder";
import { Rule, RuleObject } from "antd/es/form";
import { DatePicker } from "antd";
import dayjs from "dayjs";

interface FieldProps {
  path: string;
  value?: string;
  onUpdate: (path: string, value: string) => void;
  placeholder?: string;
  options?: string[];
  rules?: Rule[];
  type?: "text" | "date" | "password";
}

// Export interface này để bên cha (StaffAddPage) có thể dùng để định nghĩa kiểu cho useRef
export interface FieldRef {
  validate: () => Promise<boolean>;
}

// Dùng forwardRef và nhận tham số ref
export const Field = forwardRef<FieldRef, FieldProps>((props, ref) => {
  const {
    path,
    value = "",
    onUpdate,
    placeholder,
    options,
    rules,
    type = "text",
  } = props;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleValidate = async (currentValue: string): Promise<boolean> => {
    if (!rules || rules.length === 0) {
      setErrorMessage(null);
      return true;
    }

    for (const rule of rules) {
      // Kiểm tra required
      if (typeof rule === "object" && "required" in rule && rule.required) {
        if (!currentValue || currentValue.trim() === "") {
          const msg = (rule.message as string) || "Trường này là bắt buộc";
          setErrorMessage(msg);
          return false;
        }
      }

      // Kiểm tra pattern
      if (
        typeof rule === "object" &&
        "pattern" in rule &&
        rule.pattern instanceof RegExp
      ) {
        if (currentValue && !rule.pattern.test(currentValue)) {
          setErrorMessage((rule.message as string) || "Định dạng không hợp lệ");
          return false;
        }
      }

      // Kiểm tra validator
      if (typeof rule === "object" && "validator" in rule && rule.validator) {
        try {
          await rule.validator({} as RuleObject, currentValue, () => {});
        } catch (err: unknown) {
          let finalMessage = "Lỗi xác thực";

          if (err instanceof Error) {
            finalMessage = err.message;
          } else if (typeof err === "string") {
            finalMessage = err;
          }
          setErrorMessage(finalMessage);
          return false;
        }
      }
    }
    setErrorMessage(null);
    return true;
  };

  // Công khai hàm validate ra ngoài
  useImperativeHandle(ref, () => ({
    validate: () => handleValidate(value),
  }));

  return (
    <div className="relative flex flex-col w-full">
      {type === "date" ? (
        <DatePicker
          variant="borderless"
          className="cursor-pointer rounded-none px-0 py-1 w-full text-[14px] font-medium hover:border-blue-500 focus:border-blue-500"
          placeholder={placeholder || "Chọn ngày"}
          style={{
            borderBottom: "1px solid #d1d5db",
          }}
          value={value ? dayjs(value) : null}
          onChange={(date) => {
            const dateString = date ? date.format("YYYY-MM-DD") : "";
            onUpdate(path, dateString);
            handleValidate(dateString);
          }}
          format="DD/MM/YYYY"
        />
      ) : (
        <InputBottomBorder
        // type={type === "password" ? "password" : "text"}
          value={value}
          options={options || []}
          onChangeText={(text: string) => {
            onUpdate(path, text);
            handleValidate(text);
          }}
          onSelect={(val: string | number) => {
            const strVal = String(val);
            onUpdate(path, strVal);
            handleValidate(strVal);
          }}
          placeholder={placeholder}
          searchable={true}
          status={errorMessage ? "error" : "default"}
        />
      )}

      {errorMessage && (
        <span className="absolute -bottom-4 left-0 text-[10px] text-red-500 whitespace-nowrap">
          {errorMessage}
        </span>
      )}
    </div>
  );
});

// Thêm displayName để debug dễ hơn trong React DevTools
Field.displayName = "Field";
